/**
 * Runtime auth API for published / Vibe sites (mirrors ms-sitecollections' public runtime API).
 *
 * Talks to the JSON auth endpoints (`/{rts|ms}/auth/public/*`) same-origin: the monolith proxies
 * the request and ms-auth resolves the tenant from the site domain. Success resolves with the
 * authenticated user; failures throw a typed {@link AuthError} (no redirect/status mechanics leak
 * into the surface). `prefix` is `rts` on the published runtime, `ms` at design time.
 */
declare global {
    interface Window {
        Parameters?: {
            SiteAlias?: string;
            isRuntimeServer?: boolean;
        };
    }
}

export interface AuthUser {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    siteAlias: string;
    authorities: string[];
    metadata: Record<string, unknown>;
    /**
     * Whether the visitor has confirmed their email address (SNIPDUDE-7666). A site can gate whatever it
     * wants on it; ms-auth itself never blocks an unverified visitor.
     */
    emailVerified: boolean;
}

export type AuthErrorCode =
    | 'INVALID_CREDENTIALS'
    | 'USER_ALREADY_EXISTS'
    | 'FORBIDDEN'
    | 'BAD_REQUEST'
    | 'TOO_MANY_ATTEMPTS'
    | 'NETWORK'
    | 'UNKNOWN';

export class AuthError extends Error {
    constructor(
        readonly status: number,
        readonly code: AuthErrorCode,
        message: string
    ) {
        super(message);
        this.name = 'AuthError';
    }
}

const STATUS_TO_CODE: Record<number, AuthErrorCode> = {
    400: 'BAD_REQUEST',
    401: 'INVALID_CREDENTIALS',
    403: 'FORBIDDEN',
    409: 'USER_ALREADY_EXISTS',
    429: 'TOO_MANY_ATTEMPTS',
};

// Base path for the JSON auth endpoints. On the published runtime the monolith proxies
// `/rts/auth/public/*` and ms-auth resolves the tenant from the site domain. In the editor preview
// there is no published domain, so the per-site `/ms/auth/public/preview/{siteAlias}/*` surface is used
// where the gateway derives the X-Preview-Site-Alias header from the preview iframe Referer.
function authPublicBase(): string {
    if (window.Parameters?.isRuntimeServer) {
        return '/rts/auth/public';
    }
    return `/ms/auth/public/preview/${window.Parameters?.SiteAlias ?? ''}`;
}

// Maps the snake_case JSON the sign-in / sign-up / password-reset endpoints return.
// authorities/metadata are validated at the write boundary (SiteUserProfileValidator) and always
// present in the responses — map plainly, like the identity fields.
function snakeCaseToAuthUser(data: Record<string, unknown>): AuthUser {
    return {
        id: data.id as string,
        email: data.email as string,
        firstName: data.first_name as string | undefined,
        lastName: data.last_name as string | undefined,
        siteAlias: data.site_alias as string,
        authorities: (data.authorities as string[] | undefined) ?? [],
        metadata: (data.metadata as Record<string, unknown> | undefined) ?? {},
        emailVerified: data.email_verified === true,
    };
}

// The current-user endpoint returns the camelCase `UserPassport`, not the snake_case shape the
// JSON sign-in/sign-up endpoints use, so it needs its own mapper.
function passportToAuthUser(data: Record<string, unknown>): AuthUser {
    return {
        id: data.id as string,
        email: data.email as string,
        firstName: data.firstName as string | undefined,
        lastName: data.lastName as string | undefined,
        siteAlias: data.siteAlias as string,
        authorities: (data.authorities as string[] | undefined) ?? [],
        metadata: (data.metadata as Record<string, unknown> | undefined) ?? {},
        emailVerified: data.emailVerified === true,
    };
}

async function toAuthError(response: Response): Promise<AuthError> {
    let message = response.statusText;
    try {
        const body = await response.json();
        message = body?.message ?? body?.error?.message ?? message;
    } catch {
        // non-JSON error body — keep statusText
    }
    return new AuthError(
        response.status,
        STATUS_TO_CODE[response.status] ?? 'UNKNOWN',
        message
    );
}

// Network-level failures (offline, DNS, CORS) reject with a raw TypeError; log them and convert
// to the same typed AuthError surface callers already handle, so nothing untyped escapes.
async function doFetch(path: string, init: RequestInit): Promise<Response> {
    try {
        return await fetch(path, init);
    } catch (error) {
        // Brand-free prefix: this logs to the console of white-labeled published sites.
        console.error(`[auth] request to ${path} failed`, error);
        throw new AuthError(
            0,
            'NETWORK',
            error instanceof Error ? error.message : 'Network request failed'
        );
    }
}

async function postJson<T>(
    path: string,
    body: Record<string, unknown>
): Promise<T> {
    const response = await doFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw await toAuthError(response);
    }
    if (response.status === 204) {
        return undefined as T;
    }
    return (await response.json()) as T;
}

export function signIn(email: string, password: string): Promise<AuthUser> {
    return postJson<Record<string, unknown>>(`${authPublicBase()}/session`, {
        email,
        password,
    }).then(snakeCaseToAuthUser);
}

export function signUp(
    email: string,
    password: string,
    opts: {
        firstName?: string;
        lastName?: string;
        captchaToken?: string;
        isInvisibleCaptcha?: boolean;
    } = {}
): Promise<AuthUser> {
    return postJson<Record<string, unknown>>(`${authPublicBase()}/users`, {
        email,
        password,
        firstName: opts.firstName,
        lastName: opts.lastName,
        captchaToken: opts.captchaToken,
        isInvisibleCaptcha: opts.isInvisibleCaptcha ?? true,
    }).then(snakeCaseToAuthUser);
}

export function logout(): Promise<void> {
    return postJson<void>(`${authPublicBase()}/users/logout`, {});
}

/**
 * The currently authenticated site visitor, or `null` when there is no session — so callers can
 * use it to check login state without handling an error. Backed by `GET /{prefix}/auth/public/
 * users/me`, which returns the visitor's passport (camelCase) and a 401 when unauthenticated.
 */
export async function me(): Promise<AuthUser | null> {
    const response = await doFetch(`${authPublicBase()}/users/me`, {
        credentials: 'same-origin',
    });
    if (response.status === 401) {
        return null;
    }
    if (!response.ok) {
        throw await toAuthError(response);
    }
    return passportToAuthUser(await response.json());
}

export function requestPasswordReset(email: string): Promise<void> {
    return postJson<void>(
        `${authPublicBase()}/password-reset-requests`,
        { email }
    );
}

export function applyNewPassword(
    token: string,
    newPassword: string
): Promise<AuthUser> {
    return postJson<Record<string, unknown>>(
        `${authPublicBase()}/password-resets`,
        { token, newPassword }
    ).then(snakeCaseToAuthUser);
}
